import { Epic, ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import {
  INITIATE_BULK_UPLOAD_FILES_FAILURE,
  INITIATE_BULK_UPLOAD_FILES_REQUEST,
} from '../../../actions/types';
import {
  initiateBulkUploadFilesAction,
  filePartsUploadAction,
} from '../../../actions';
import { InitiateBatchReportUploadOutput } from '@amzn/coral_com-amazon-transreportscenterservice';
import { CHUNKS_SIZE } from '../../../constants/upload';
import { addNotification } from '../../../actions/ui';
import {
  DEFAULT_NOTIFICATION_CLOSE_TIMEOUT,
  NotificationTypeEnum,
} from '../../../actions/ui/types';
import { reportsCenterErrorCodes } from '../../../config/errorCodeMessages';

export const initiateBulkUploadFilesEpic: Epic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(INITIATE_BULK_UPLOAD_FILES_REQUEST),
    switchMap(({ payload }) => {
      const { initiateBatchReportUploadInput, files } = payload;

      return from(
        api.reportsCenterService
          .initiateBatchReportUpload(initiateBatchReportUploadInput)
          .pipe(
            mergeMap(
              (
                initiateBatchReportUploadOutput: InitiateBatchReportUploadOutput
              ) => {
                const {
                  InitiateUploadResponseList: initiateUploadResponseList,
                } = initiateBatchReportUploadOutput || {};

                const parallelPartsUploadObservables =
                  initiateUploadResponseList.map(
                    (initiateUploadResponse, index) => {
                      const requiredFile = files[index];
                      const partUploadRequests = [];
                      let sentSize = 0;
                      if (requiredFile) {
                        initiateUploadResponse.partPresignedURLs.forEach(
                          (partPresignedURL, index) => {
                            sentSize = index * CHUNKS_SIZE;
                            partUploadRequests.push(
                              api.reportsCenterService.uploadWithS3PresignedUrl(
                                {
                                  file: requiredFile.slice(
                                    sentSize,
                                    sentSize + CHUNKS_SIZE
                                  ),
                                  s3presignedUrl: partPresignedURL,
                                }
                              )
                            );
                          }
                        );
                      }
                      return {
                        partUploadRequests,
                        uploadId: initiateUploadResponse.uploadId,
                        reportId: initiateUploadResponse?.reportId,
                        partPresignedURLs:
                          initiateUploadResponse.partPresignedURLs,
                        key: initiateBatchReportUploadInput
                          .InitiateUploadRequestList[index].key,
                        supportingDocumentInfo:
                          initiateBatchReportUploadInput
                            .InitiateUploadRequestList[index]
                            .supportingDocumentInfo,
                        logisticsPartyId:
                          initiateBatchReportUploadInput.logisticsPartyId,
                        reportType: initiateBatchReportUploadInput.reportType,
                        term: initiateBatchReportUploadInput.term,
                      };
                    }
                  );

                const filePartsUploadActions =
                  parallelPartsUploadObservables.map(
                    (parallelPartsUploadObservable) =>
                      filePartsUploadAction.request(
                        parallelPartsUploadObservable
                      )
                  );
                return of(
                  initiateBulkUploadFilesAction.success({
                    initiateBatchReportUploadOutput,
                  }),
                  ...filePartsUploadActions
                );
              }
            ),
            catchError((err) =>
              of(
                initiateBulkUploadFilesAction.failure(err),
                addNotification({
                  type: NotificationTypeEnum.ERROR,
                  action: INITIATE_BULK_UPLOAD_FILES_FAILURE,
                  body: err?.receivedException?.errorCode
                    ? reportsCenterErrorCodes[
                        err?.receivedException?.errorCode
                      ] ?? 'initiate_upload_files_notifications.failure'
                    : 'initiate_upload_files_notifications.failure',
                  timeout: DEFAULT_NOTIFICATION_CLOSE_TIMEOUT,
                })
              )
            )
          )
      );
    })
  );

export default initiateBulkUploadFilesEpic;
